import { useCallback } from 'react';
import { getDataFromLs, removeDataFromLs, setDataToLs } from '@/helpers';
import { impersonationService } from '@/services/impersonation.service';
import { type CurrentScopeListedItem, LsStoreKey, type ScopeListedItem } from '@/types';
import { useGlobalSettings } from '@/hooks';

export const useLastSelectedChurch = () => {
  const { setCurrentChurch } = useGlobalSettings();

  const getLastSelectedChurch = useCallback((): ScopeListedItem | null => {
    return getDataFromLs(LsStoreKey.LastSelectedChurch);
  }, []);

  const setLastSelectedChurch = useCallback(
    (scope: ScopeListedItem) => {
      const previousChurch = getLastSelectedChurch();
      const isChurchChanged = Boolean(previousChurch?.churchId && previousChurch.churchId !== scope.churchId);

      setDataToLs(LsStoreKey.LastSelectedChurch, scope);
      setCurrentChurch(scope);

      if (isChurchChanged) {
        setDataToLs(LsStoreKey.SelectedCalendar, 'all');
      }

      if (impersonationService.getSession().isImpersonating) {
        impersonationService.setChurchId({ churchId: scope.churchId });
      }
    },
    [getLastSelectedChurch, setCurrentChurch]
  );

  const initializeLastSelectedChurch = useCallback(
    (currentScope: CurrentScopeListedItem[]) => {
      const isImpersonating = impersonationService.getSession().isImpersonating;

      const applyChurchFromScope = (churchId: string): boolean => {
        const normalizedChurchId = churchId.trim();
        if (!normalizedChurchId || currentScope.length === 0) {
          return false;
        }

        const churchFromScope = currentScope.find((scope) => scope.churchId === normalizedChurchId);
        if (!churchFromScope) {
          return false;
        }

        setCurrentChurch(churchFromScope);
        setLastSelectedChurch(churchFromScope);
        return true;
      };

      const loginChurchId = (getDataFromLs(LsStoreKey.LoginChurchId) as string | undefined)?.trim() || '';
      if (loginChurchId) {
        if (applyChurchFromScope(loginChurchId)) {
          removeDataFromLs(LsStoreKey.LoginChurchId);
          return;
        }

        removeDataFromLs(LsStoreKey.LoginChurchId);

        if (isImpersonating) {
          const sessionChurchId = impersonationService.getSession().churchId?.trim() || '';
          if (sessionChurchId) {
            applyChurchFromScope(sessionChurchId);
          }
          return;
        }
      }

      if (isImpersonating) {
        const sessionChurchId = impersonationService.getSession().churchId?.trim() || '';
        if (sessionChurchId) {
          applyChurchFromScope(sessionChurchId);
        }
        return;
      }

      const lastSelectedChurch = getLastSelectedChurch();

      if (lastSelectedChurch && lastSelectedChurch.churchId) {
        // Find the church in currentScope to get fresh data with allMembersGroupId
        const churchFromScope = currentScope.find((scope) => scope.churchId === lastSelectedChurch.churchId);

        if (churchFromScope) {
          setCurrentChurch(churchFromScope);
          // Update localStorage with fresh data including allMembersGroupId
          setLastSelectedChurch(churchFromScope);
        } else {
          setCurrentChurch(lastSelectedChurch);
        }
      } else if (currentScope && currentScope.length > 0) {
        const firstChurch = currentScope.find(({ churchName }) => churchName);
        if (firstChurch) {
          setCurrentChurch(firstChurch);
          setLastSelectedChurch(firstChurch);
        }
      }
    },
    [getLastSelectedChurch, setCurrentChurch, setLastSelectedChurch]
  );

  return {
    getLastSelectedChurch,
    setLastSelectedChurch,
    initializeLastSelectedChurch,
  };
};
